import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";

const empty = {
    employee_id: "",
    date: new Date().toISOString().slice(0, 10),
    hours_worked: 6,
    on_time: true,
    coperti: 0,
    revenue: 0,
    tips: 0,
    rating: 4,
    notes: "",
};

export default function PerformancePage() {
    const [employees, setEmployees] = useState([]);
    const [perfs, setPerfs] = useState([]);
    const [employeeFilter, setEmployeeFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);

    const loadEmployees = async () => {
        const { data } = await api.get("/employees");
        setEmployees(data.items);
    };
    const load = async () => {
        const params = {};
        if (employeeFilter !== "all") params.employee_id = employeeFilter;
        const { data } = await api.get("/performance", { params });
        setPerfs(data.items);
    };

    useEffect(() => {
        loadEmployees();
    }, []);
    useEffect(() => {
        load();
    }, [employeeFilter]);

    const save = async () => {
        try {
            await api.post("/performance", form);
            toast.success("Performance registrata");
            setOpen(false);
            setForm(empty);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/performance/${id}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const empMap = useMemo(
        () => Object.fromEntries(employees.map((e) => [e.id, e])),
        [employees]
    );

    const trend = useMemo(() => {
        const byDate = {};
        for (const p of perfs) {
            byDate[p.date] = byDate[p.date] || {
                date: p.date,
                coperti: 0,
                revenue: 0,
                tips: 0,
            };
            byDate[p.date].coperti += p.coperti || 0;
            byDate[p.date].revenue += p.revenue || 0;
            byDate[p.date].tips += p.tips || 0;
        }
        return Object.values(byDate).sort((a, b) =>
            a.date.localeCompare(b.date)
        );
    }, [perfs]);

    const ratingByEmp = useMemo(() => {
        const m = {};
        for (const p of perfs) {
            m[p.employee_id] = m[p.employee_id] || { sum: 0, n: 0 };
            m[p.employee_id].sum += p.rating || 0;
            m[p.employee_id].n += 1;
        }
        return Object.entries(m)
            .map(([eid, v]) => ({
                name: empMap[eid]?.name || "—",
                rating: +(v.sum / v.n).toFixed(2),
            }))
            .sort((a, b) => b.rating - a.rating);
    }, [perfs, empMap]);

    return (
        <div data-testid="performance-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
                <div>
                    <div className="label-eyebrow mb-2">KPI sala</div>
                    <h1 className="font-display text-4xl text-navy">
                        Performance
                    </h1>
                    <p className="text-sf-secondary mt-1">
                        Coperti, scontrino medio, mance, puntualità, rating.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select
                        value={employeeFilter}
                        onValueChange={setEmployeeFilter}
                    >
                        <SelectTrigger
                            className="w-56"
                            data-testid="performance-emp-filter"
                        >
                            <SelectValue placeholder="Tutti i dipendenti" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                Tutti i dipendenti
                            </SelectItem>
                            {employees.map((e) => (
                                <SelectItem key={e.id} value={e.id}>
                                    {e.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-navy text-white lift"
                                data-testid="performance-add-btn"
                            >
                                <Plus size={16} className="mr-2" /> Registra
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="performance-dialog" className="max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">
                                    Nuovo record performance
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <Field label="Dipendente">
                                    <Select
                                        value={form.employee_id}
                                        onValueChange={(v) =>
                                            setForm({
                                                ...form,
                                                employee_id: v,
                                            })
                                        }
                                    >
                                        <SelectTrigger data-testid="performance-form-emp">
                                            <SelectValue placeholder="Seleziona" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {employees.map((e) => (
                                                <SelectItem
                                                    key={e.id}
                                                    value={e.id}
                                                >
                                                    {e.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Data">
                                        <Input
                                            type="date"
                                            value={form.date}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    date: e.target.value,
                                                })
                                            }
                                            data-testid="performance-form-date"
                                        />
                                    </Field>
                                    <Field label="Ore lavorate">
                                        <Input
                                            type="number"
                                            step="0.25"
                                            value={form.hours_worked}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    hours_worked: parseFloat(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="performance-form-hours"
                                        />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <Field label="Coperti">
                                        <Input
                                            type="number"
                                            value={form.coperti}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    coperti: parseInt(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="performance-form-coperti"
                                        />
                                    </Field>
                                    <Field label="Incasso (€)">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={form.revenue}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    revenue: parseFloat(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="performance-form-revenue"
                                        />
                                    </Field>
                                    <Field label="Mance (€)">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={form.tips}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    tips: parseFloat(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="performance-form-tips"
                                        />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3 items-end">
                                    <Field label="Rating (0-5)">
                                        <Input
                                            type="number"
                                            min={0}
                                            max={5}
                                            step="0.1"
                                            value={form.rating}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    rating: parseFloat(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="performance-form-rating"
                                        />
                                    </Field>
                                    <div className="flex items-center gap-3 pt-7">
                                        <Switch
                                            checked={form.on_time}
                                            onCheckedChange={(v) =>
                                                setForm({
                                                    ...form,
                                                    on_time: v,
                                                })
                                            }
                                            data-testid="performance-form-ontime"
                                        />
                                        <span className="text-sm text-sf-secondary">
                                            Puntuale
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setOpen(false)}
                                >
                                    Annulla
                                </Button>
                                <Button
                                    className="bg-navy text-white"
                                    onClick={save}
                                    data-testid="performance-form-save"
                                >
                                    Salva
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-6">
                <div
                    className="bg-white border border-sf rounded-xl p-6"
                    data-testid="perf-chart-trend"
                >
                    <div className="label-eyebrow mb-4">
                        Andamento incassi & coperti
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <LineChart data={trend}>
                                <CartesianGrid
                                    stroke="#e2e8f0"
                                    strokeDasharray="3 3"
                                />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Line
                                    type="monotone"
                                    dataKey="revenue"
                                    name="€ Incasso"
                                    stroke="#0a2342"
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="coperti"
                                    name="Coperti"
                                    stroke="#f0a924"
                                    strokeWidth={2}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div
                    className="bg-white border border-sf rounded-xl p-6"
                    data-testid="perf-chart-rating"
                >
                    <div className="label-eyebrow mb-4">
                        Rating medio per dipendente
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={ratingByEmp}>
                                <CartesianGrid
                                    stroke="#e2e8f0"
                                    strokeDasharray="3 3"
                                />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis
                                    domain={[0, 5]}
                                    tick={{ fontSize: 11 }}
                                />
                                <Tooltip />
                                <Bar dataKey="rating" fill="#f0a924" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div
                className="bg-white border border-sf rounded-xl overflow-hidden"
                data-testid="perf-table"
            >
                <table className="w-full text-sm">
                    <thead className="bg-[#fafaf7]">
                        <tr className="text-left">
                            <th className="px-4 py-3 label-eyebrow">Data</th>
                            <th className="px-4 py-3 label-eyebrow">
                                Dipendente
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Ore
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Coperti
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Incasso
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Scontr. medio
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Mance
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Rating
                            </th>
                            <th className="px-4 py-3 label-eyebrow">Punt.</th>
                            <th className="px-4 py-3 label-eyebrow"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {perfs.length === 0 && (
                            <tr>
                                <td
                                    colSpan={10}
                                    className="text-center py-12 text-sf-muted"
                                >
                                    Nessun record. Registra la prima
                                    performance.
                                </td>
                            </tr>
                        )}
                        {perfs.map((p) => (
                            <tr
                                key={p.id}
                                className="border-t border-sf hover:bg-[#fafaf7]"
                                data-testid={`perf-row-${p.id}`}
                            >
                                <td className="px-4 py-3 font-mono text-xs">
                                    {p.date}
                                </td>
                                <td className="px-4 py-3 font-medium">
                                    {empMap[p.employee_id]?.name || "—"}
                                </td>
                                <td className="px-4 py-3 text-right font-mono">
                                    {p.hours_worked}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    {p.coperti}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    €{p.revenue?.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    €{p.scontrino_medio?.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    €{p.tips?.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right text-amber-brand font-semibold">
                                    {p.rating}
                                </td>
                                <td className="px-4 py-3">
                                    {p.on_time ? "✓" : "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        onClick={() => remove(p.id)}
                                        className="text-red-600 p-1"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-wider text-sf-muted">
                {label}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
